import React, { useEffect, useState } from 'react';
import { Box, Text, Heading, Button, ButtonSize } from '@bechtel/gds';
import { useEnvProvider, useAppUser, useConfig } from '@bechtel/client-common';
import { map } from 'lodash';
import { Trans } from '@lingui/macro';
import { Map, MarkerType, Marker } from '@bechtel/client-weld-common';
import { MapContainerStyled } from './styled';
import { ProjectAdminContainerStyled, ProjectAdminSubContainerStyled, ProjectAdminHeaderStyled } from '../styled';
import { DataVaultItem } from './item';
import DataVaultEditIcon from '@bechtel/client-assets/dist/images/data-vault-edit.svg';
import DataVaultIcon from '@bechtel/client-assets/dist/images/data-vault-icon.svg';
import DataVaultInactiveIcon from '@bechtel/client-assets/dist/images/data-vault-inactive.svg';
import { DataVault } from './types';
import { AddNewDataVault } from './add';

export const dataVaultList: DataVault[] = [
  {
    id: '11',
    name: 'Data Vault 1',
    location: {
      latitude: 11.22,
      longitude: 23.11,
    },
    locationDescription: 'This data vault is located next to pillar 18 in OSBL.',
    isActive: true,
  },
  {
    id: '12',
    name: 'Data Vault 2',
    location: {
      latitude: 20.22,
      longitude: 23.11,
    },
    locationDescription: 'Data vault description 2',
    isActive: false,
  },
  {
    id: '14',
    name: 'Data Vault 3',
    location: {
      latitude: 40.22,
      longitude: 23.11,
    },
    isActive: true,
  },
  {
    id: '15',
    name: 'Data Vault 4',
    location: {
      latitude: 11.22,
      longitude: 13.11,
    },
    locationDescription: 'Data vault description 4',
    isActive: true,
  },
];

const getMarkers = (dataVaults: DataVault[], selectedDataVaultIds: Set<string>): Marker[] => {
  return map(dataVaults, (dataVault) => {
    return {
      id: dataVault.id,
      markerType: MarkerType.ROD_ROOM,
      position: { lat: dataVault.location?.latitude, lng: dataVault.location?.longitude },
      icon: selectedDataVaultIds.has(dataVault.id)
        ? DataVaultEditIcon
        : dataVault.isActive
        ? DataVaultIcon
        : DataVaultInactiveIcon,
    };
  });
};

export const DataVaults = () => {
  const [selectedDataVaultIds, setSelectedDataVaultIds] = useState<Set<string>>(new Set([]));
  const { envConfig } = useEnvProvider();
  const { features } = useConfig();
  const { user } = useAppUser();
  const [markerList, setMarkerList] = useState<Marker[] | []>([]);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [addDataVault, setAddDataVault] = useState(false);

  useEffect(() => {
    const markers = getMarkers(dataVaultList, selectedDataVaultIds);
    if (markers.length) {
      const mapCenter: google.maps.LatLngLiteral = user?.project?.projectConfiguration.geoLocation
        ? {
            lat: user.project.projectConfiguration.geoLocation?.latitude,
            lng: user.project.projectConfiguration.geoLocation?.longitude,
          }
        : { lat: markers[0]['position']['lat'], lng: markers[0]['position']['lng'] };
      setMarkerList(markers);
      setMapCenter(mapCenter);
    }
  }, [selectedDataVaultIds, dataVaultList]);

  return (
    <ProjectAdminContainerStyled>
      <ProjectAdminHeaderStyled>
        <Text family="graphik" size="600" weight="600" color="white">
          <Trans>Data Vaults</Trans>
        </Text>
      </ProjectAdminHeaderStyled>

      {dataVaultList.length > 0 && (
        <ProjectAdminSubContainerStyled mt={{ sm: '450' }} data-testid="module-data-container">
          <Box my={{ sm: '550' }} mx={{ sm: '600' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
            <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
              <Box flexDirection={{ sm: 'column' }}>
                <Heading family="graphik" size="500" weight="600">
                  <Trans>Manage Data Vaults</Trans>
                </Heading>
                <Box mt={{ sm: '400' }}>
                  <Text family="graphik" size="300">
                    <Trans>Add, Manage Data Vaults for a project</Trans>
                  </Text>
                </Box>
              </Box>
              <Button
                disabled={addDataVault}
                css={{ width: '150px' }}
                size={ButtonSize.LARGE}
                onClick={() => setAddDataVault(true)}
              >
                Add Data Vault
              </Button>
            </Box>
            {features?.showMapView && markerList.length > 0 && (
              <MapContainerStyled mt={{ sm: '550' }}>
                <Map
                  apiKey={envConfig?.mapApiKey}
                  zoom={16}
                  center={mapCenter}
                  markers={markerList}
                  mapTypeId="satellite"
                />
              </MapContainerStyled>
            )}
            {map(dataVaultList, (dataVault: DataVault) => {
              return (
                <DataVaultItem
                  key={dataVault.id}
                  dataVault={dataVault}
                  setSelectedDataVaultIds={setSelectedDataVaultIds}
                />
              );
            })}
            {addDataVault && <AddNewDataVault close={() => setAddDataVault(false)} />}
          </Box>
        </ProjectAdminSubContainerStyled>
      )}
    </ProjectAdminContainerStyled>
  );
};
